import type {
  NotificationChannel,
  NotificationSetting,
  NotificationSettings,
} from "../../api/types";

export interface NotificationEventType {
  key: string;
  label: string;
  description: string;
  /** `docs/spec/notifications.md` §4. Three of these are deliberately off. */
  defaultOn: boolean;
  /**
   * Which channels the event can be delivered on. The `emails.*` family is mail
   * only — there is no inbox row for "your export finished" — so those rows
   * carry a single boolean rather than a per-channel map.
   */
  channels: NotificationChannel[];
}

const BOTH: NotificationChannel[] = ["app", "email"];
const MAIL: NotificationChannel[] = ["email"];

export interface NotificationGroup {
  name: string;
  events: NotificationEventType[];
}

export const notificationGroups: NotificationGroup[] = [
  {
    name: "Documents and collections",
    events: [
      {
        key: "documents.publish",
        label: "A document is published",
        description: "Anywhere in the workspace. Off by default — it is a lot of mail.",
        defaultOn: false,
        channels: BOTH,
      },
      {
        key: "documents.update",
        label: "A document I follow changes",
        description: "At most one notification per document every six hours.",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "documents.add_user",
        label: "I am given access to a document",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "documents.mentioned",
        label: "I am mentioned in a document",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "documents.group_mentioned",
        label: "A group I belong to is mentioned in a document",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "revisions.create",
        label: "Every revision of a document I follow",
        description: "Finer-grained than the update notification. Off by default.",
        defaultOn: false,
        channels: BOTH,
      },
      {
        key: "collections.create",
        label: "A collection is created",
        description: "Off by default.",
        defaultOn: false,
        channels: BOTH,
      },
      {
        key: "collections.add_user",
        label: "I am given access to a collection",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
    ],
  },
  {
    name: "Comments",
    events: [
      {
        key: "comments.create",
        label: "A comment is added",
        description: "On a document I follow, or a reply in a thread I am part of.",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "comments.mentioned",
        label: "I am mentioned in a comment",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "comments.group_mentioned",
        label: "A group I belong to is mentioned in a comment",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "comments.resolve",
        label: "A thread I took part in is resolved",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
      {
        key: "reactions.create",
        label: "Someone reacts to my comment",
        description: "",
        defaultOn: true,
        channels: BOTH,
      },
    ],
  },
  {
    name: "Email only",
    events: [
      {
        key: "emails.invite_accepted",
        label: "Someone I invited joins",
        description: "",
        defaultOn: true,
        channels: MAIL,
      },
      {
        key: "emails.export_completed",
        label: "My export finishes",
        description: "",
        defaultOn: true,
        channels: MAIL,
      },
      {
        key: "emails.onboarding",
        label: "Getting-started series",
        description: "",
        defaultOn: true,
        channels: MAIL,
      },
      {
        key: "emails.features",
        label: "Product announcements",
        description: "",
        defaultOn: true,
        channels: MAIL,
      },
    ],
  },
];

export const notificationEventTypes: NotificationEventType[] = notificationGroups.flatMap(
  (group) => group.events,
);

/**
 * `docs/spec/notifications.md` §5, example D: an object uses its channel key
 * when present and the default otherwise; a boolean applies to every channel; an
 * absent key — and an object with neither channel set — falls back to the
 * default.
 */
export function resolveSetting(
  value: NotificationSetting | undefined,
  channel: NotificationChannel,
  defaultOn: boolean,
): boolean {
  if (value === undefined) return defaultOn;
  if (typeof value === "boolean") return value;
  const app = value.app;
  const email = value.email;
  if (app === undefined && email === undefined) return defaultOn;
  return (channel === "app" ? app : email) ?? defaultOn;
}

export interface SettingChange {
  key: string;
  channel: NotificationChannel;
  value: boolean;
}

/**
 * The whole map, resolved against the defaults, with one change applied. The
 * settings screen sends every key rather than a patch so that what the user sees
 * and what is stored cannot drift apart as defaults change.
 */
export function fullSettingsMap(
  current: NotificationSettings | undefined,
  change?: SettingChange,
): NotificationSettings {
  const next: NotificationSettings = {};
  for (const event of notificationEventTypes) {
    const stored = current?.[event.key];
    const resolved: Record<string, boolean> = {};
    for (const channel of event.channels) {
      const changed = change?.key === event.key && change.channel === channel;
      resolved[channel] =
        changed && change !== undefined
          ? change.value
          : resolveSetting(stored, channel, event.defaultOn);
    }
    next[event.key] =
      event.channels.length === 1 ? (resolved[event.channels[0] ?? "email"] ?? false) : resolved;
  }
  return next;
}
